import { graphql } from 'gatsby';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import MainSection100 from '../components/Layout/main-section-100';
import MainSection50 from '../components/Layout/main-section-50';
import NewLayout from '../components/new_layout';
import ReadyToTalk from '../components/v2023/ReadyToTalk/ready-to-talk';
import CardPost from '../components/v2023/UI/Card/card-post';
import ItemGroup from '../components/v2023/UI/Item/item-group';
import { Comparative } from '../components/v2024/comparative';
import ImageGroup from '../components/v2024/image/image-group';
import ListHighlight from '../components/v2024/list/list-highlight';
import ModalLogin from '../components/v2024/Login/modal-login';
import MenuModalState from '../components/v2024/Login/modal-login-state';
import { ModalForm } from '../components/v2024/modal';
import { WhiteTileNb } from '../components/v2024/tile/tile';
import { mountAvatarObject } from '../utils/avatar-utils';
import { formatDate } from '../utils/date-utils';
import SEO from '../components/seo';

const ComparativeCompetitor = ({ data, pageContext }) => {
	const lang = pageContext.langKey;
	const images = pageContext.images;
	const comparativeCompetitor = data.comparativeCompetitor;

	// The pages for which the special offer logic applies
	const pagesWithOffer = [
		'logalty-firma-electronica-contratacion-digital-notificaciones-comunicaciones-identificacion',
		'docuten-firma-electronica',
		'firmafy-firma-electronica-empresas-eidas',
		'viafirma-firma-electronica-empresas-desatendida',
		'docusign-firma-electronica',
		'signaturit-firma-electronica',
		'pandadoc-firma-electronica',
	];

	useEffect(() => {
		if (typeof window !== 'undefined') {
			const params = new URLSearchParams(window.location.search);
			if (params.has('firma_electronica')) {
				setShowComparative('sign');
			} else if (params.has('verificacion_identidad')) {
				setShowComparative('verification');
			} else {
				//no se hace nada
			}
		}
	}, []);

	// Top bar and label texts in both Spanish and English
	// You can adjust these as needed
	const topBarText =
		lang === 'es'
			? '<strong>Sólo hoy 6 meses gratis</strong> al solicitar una demo o prueba de 14 días'
			: '<strong>Only today: 6 free months</strong> by requesting a demo or 14-day trial';

	const heroTryText = lang === 'es' ? 'Probar 6 meses' : 'Get 6 months free';

	const heroCallText = lang === 'es' ? 'Solicita una llamada' : 'Request a call';

	const extraTextUnderHero =
		lang === 'es'
			? 'Se te asignarán <strong>6 meses gratis</strong> si te registras <span class="underline">durante el día de hoy</span>. Activación 24h/48h.'
			: 'You will get <strong>6 free months</strong> if you sign up <u>today</u>. Activation in 24/48h.';

	// For the countdown timer
	const [timeLeft, setTimeLeft] = useState({ hours: 0, minutes: 0, seconds: 0 });

	useEffect(() => {
		const calculateTimeLeft = () => {
			const now = new Date();
			const tomorrow = new Date();
			// set to next 00:00:00
			tomorrow.setHours(24, 0, 0, 0);
			const diff = tomorrow - now;

			if (diff > 0) {
				const hours = Math.floor(diff / 1000 / 60 / 60);
				const minutes = Math.floor((diff / 1000 / 60) % 60);
				const seconds = Math.floor((diff / 1000) % 60);
				setTimeLeft({ hours, minutes, seconds });
			} else {
				// Past midnight (reset)
				setTimeLeft({ hours: 0, minutes: 0, seconds: 0 });
			}
		};

		const timer = setInterval(calculateTimeLeft, 1000);
		return () => clearInterval(timer);
	}, []);

	// Show/hide the signature or verification table
	const signAvailable = comparativeCompetitor.comparative_electronic_signature_button ? true : false;
	const verificationAvailable = comparativeCompetitor.comparative_identity_verification_button ? true : false;

	const [showComparative, setShowComparative] = useState(signAvailable ? 'sign' : 'verification');

	const isSignActive = () => showComparative === 'sign';
	const isVerificationActive = () => showComparative === 'verification';

	// Hero button styles
	const heroButtonDemoClass =
		comparativeCompetitor.hero_button_try === undefined || showComparative === 'verification'
			? 'button--ghost'
			: 'button--secondary--ghost button--secondary';

	// Modal states
	const loginModalStatus = MenuModalState();
	const talkExpertModalState = MenuModalState();
	const tryModalState = MenuModalState();

	// Build avatars for testimonials
	const signatureOpinion = mountAvatarObject(
		comparativeCompetitor.opinions_signature[0]?.name,
		comparativeCompetitor.opinions_signature[0]?.job_position,
		comparativeCompetitor.opinions_signature[0]?.photo,
	);
	const verificationOpinion = mountAvatarObject(
		comparativeCompetitor.opinions_verifications[0]?.name,
		comparativeCompetitor.opinions_verifications[0]?.job_position,
		comparativeCompetitor.opinions_verifications[0]?.photo,
	);

	// Paths in different languages
	const pathBlog = lang === 'en' ? '/blog' : `/${lang}/blog`;
	const pathWhitePapers = lang === 'en' ? '/ebooks-whitepapers' : `/${lang}/ebooks-whitepapers`;
	const pathWebinars = lang === 'en' ? '/webinars' : '/es/webinars';
	const pathGeneric = lang === 'en' ? '/' : '/es/';

	// Normalize "news" items for the CardPost components
	const normalizeData = (item) => {
		if (item.strapi_component === 'sector.blog') {
			return {
				title: item.title,
				url: `${pathBlog}/${item.blog_post.url}`,
				tag: 'Blog',
				image: { localFile: images[item.image.localFile___NODE].data.image },
				date: item.blog_post.updated_at,
			};
		}

		if (item.strapi_component === 'sector.e-book') {
			return {
				title: item.title,
				url: `${pathWhitePapers}/${item.ebook.url}`,
				tag: 'eBook',
				image: { localFile: images[item.image.localFile___NODE].data.image },
				date: item.ebook.updated_at,
			};
		}

		if (item.strapi_component === 'sector.webinars') {
			return {
				title: item.title,
				url: `${pathWebinars}/${item.webinar.url}`,
				tag: 'Webinar',
				image: { localFile: images[item.image.localFile___NODE].data.image },
				date: item.webinar.updated_at,
			};
		}

		return {
			title: item.title,
			url: `${pathGeneric}${item.generic_landing_page.url}`,
			tag: lang === 'en' ? 'Use Case' : 'Caso de uso',
			image: { localFile: images[item.image.localFile___NODE].data.image },
			date: item.generic_landing_page.updated_at,
		};
	};

	// Build objects needed for the <Comparative> component
	const normalizeComparativeData = (tecalisName, competitorName, comparative, buttons) => {
		const data = [];
		const tecalisCharacteristicsList = [];
		const competitorCharacteristicsList = [];

		comparative.forEach((item) => {
			tecalisCharacteristicsList.push({
				name: item.name,
				text: item.text_tecalis,
				bool: item.tecalis,
			});
			competitorCharacteristicsList.push({
				name: item.name,
				text: item.text_competitor,
				bool: item.competitor,
			});
		});

		data.push({
			name: tecalisName,
			highlighted: true,
			characteristicsList: { characteristics: tecalisCharacteristicsList },
			buttons: buttons,
		});
		data.push({
			name: competitorName,
			highlighted: false,
			characteristicsList: { characteristics: competitorCharacteristicsList },
			buttons: [],
		});
		return data;
	};

	// Buttons used in the comparative
	const talkExpertButton = (
		<button className="button button--lg" onClick={() => talkExpertModalState.openMenu()}>
			{comparativeCompetitor.comparative_talk_expert_button}
		</button>
	);
	const tryButton = <a onClick={() => tryModalState.openMenu()}>{comparativeCompetitor.comparative_try_button}</a>;

	// Setting up the comparative data
	const signElements = comparativeCompetitor.comparative_electronic_signature_elements;
	const signComparative = normalizeComparativeData(
		comparativeCompetitor.logo_tecalis_comparative,
		comparativeCompetitor.competitor_comparative,
		signElements,
		[talkExpertButton, tryButton],
	);

	const identityVerificationElements = comparativeCompetitor.comparative_identity_verification_elements;
	const identityVerifcationComparative = normalizeComparativeData(
		comparativeCompetitor.logo_tecalis_comparative,
		comparativeCompetitor.competitor_comparative,
		identityVerificationElements,
		[talkExpertButton],
	);

	return (
		<NewLayout noHeader pageContext={pageContext}>
			{/* 
        If comparativeCompetitor.url is in pagesWithOffer, we show the purple bar with countdown
      */}
			{pagesWithOffer.includes(comparativeCompetitor.url) && (
				<div className="banner">
					<div className="banner__body">
						<i className="icon-star"></i>
						<span dangerouslySetInnerHTML={{ __html: topBarText }} />
						<strong class="banner__body__counter">
							{String(timeLeft.hours).padStart(2, '0')}:{String(timeLeft.minutes).padStart(2, '0')}:
							{String(timeLeft.seconds).padStart(2, '0')}
						</strong>
					</div>
				</div>
			)}

			{/* SEO */}
			<SEO
				lang={lang}
				title={comparativeCompetitor.seo.title}
				description={comparativeCompetitor.seo.meta_description}
				translates={pageContext.translates}
				image={comparativeCompetitor.seo.image_2.localFile.publicURL}
			/>

			{/* HERO */}
			<MainSection50
				sectionType="color"
				sectionColor="purple-dark"
				sectionSize="lg"
				childrenLeft={
					<>
						<GatsbyImage
							alt={comparativeCompetitor.logo_tecalis.alternativeText}
							image={getImage(comparativeCompetitor.logo_tecalis.localFile)}
						/>
						<h1 className="merriweather">{comparativeCompetitor.hero_title}</h1>
						<div className="container__text">
							<ReactMarkdown
								children={comparativeCompetitor.hero_description}
								rehypePlugins={[rehypeRaw]}
							/>
						</div>

						<div className="button__wrapper">
							{/* If sign is active and the page is in the special-offer list, we change texts */}
							{comparativeCompetitor.hero_button_try && showComparative !== 'verification' && (
								<>
									<button
										className="button button--xl button--ghost"
										onClick={() => loginModalStatus.openMenu()}
									>
										{pagesWithOffer.includes(comparativeCompetitor.url)
											? heroTryText
											: comparativeCompetitor.hero_button_try}
									</button>
									<ModalLogin
										lang={lang}
										state={loginModalStatus}
										plan={comparativeCompetitor.try_form}
										selectedPlan="starter"
									/>
								</>
							)}

							<a className={`button button--xl  ${heroButtonDemoClass}`} href="#ready-to-talk">
								{pagesWithOffer.includes(comparativeCompetitor.url)
									? heroCallText
									: comparativeCompetitor.hero_button_demo}
							</a>
						</div>

						{/* Extra text about free 6 months */}
						{pagesWithOffer.includes(comparativeCompetitor.url) && (
							<span
								className="container__small-text"
								dangerouslySetInnerHTML={{ __html: extraTextUnderHero }}
							/>
						)}
					</>
				}
				childrenRight={
					<div data-aos="fade-left">
						<GatsbyImage
							alt={comparativeCompetitor.hero_image.alternativeText}
							image={getImage(comparativeCompetitor.hero_image.localFile)}
						/>
					</div>
				}
			/>

			{signAvailable && verificationAvailable && (
				<MainSection100 borderBottom>
					<div className="container__text">
						<ReactMarkdown
							children={comparativeCompetitor.comparative_description}
							rehypePlugins={[rehypeRaw]}
						/>
					</div>
					{/* Type selector */}
					<div className="tabs tabs--pills-big">
						<div
							className={`tabs__tab ${showComparative === 'sign' ? 'tabs__tab--active' : ''}`}
							onClick={() => setShowComparative('sign')}
						>
							{comparativeCompetitor.comparative_electronic_signature_button}
						</div>
						<div
							className={`tabs__tab ${showComparative === 'verification' ? 'tabs__tab--active' : ''}`}
							onClick={() => setShowComparative('verification')}
						>
							{comparativeCompetitor.comparative_identity_verification_button}
						</div>
					</div>
				</MainSection100>
			)}

			{/* Comparative */}
			<MainSection100>
				{isSignActive() && (
					<Comparative
						comparative={signComparative}
						modals={
							<>
								<ModalForm
									state={talkExpertModalState}
									divFormId="expertFormSign"
									form={comparativeCompetitor.talk_expert_form}
								/>
								<ModalLogin
									state={tryModalState}
									lang={lang}
									selectedPlan="starter"
									plan={comparativeCompetitor.try_form}
								/>
							</>
						}
					/>
				)}
				{isVerificationActive() && (
					<Comparative
						comparative={identityVerifcationComparative}
						modals={
							<ModalForm
								state={talkExpertModalState}
								divFormId="expertFormTry"
								form={comparativeCompetitor.talk_expert_form}
							/>
						}
					/>
				)}
			</MainSection100>

			{/* Trust */}
			<MainSection100>
				<h2>{comparativeCompetitor.trust_title}</h2>
				<div className="grid-md">
					{isSignActive() &&
						comparativeCompetitor.trust_logos_signature.map((logo, index) => {
							return <GatsbyImage key={index} image={getImage(logo.image.localFile)} alt={logo.alt} />;
						})}
					{isVerificationActive() &&
						comparativeCompetitor.trust_logos_verification.map((logo, index) => {
							return <GatsbyImage key={index} image={getImage(logo.image.localFile)} alt={logo.alt} />;
						})}
				</div>
			</MainSection100>

			{/* Opinion */}
			<MainSection100>
				{isSignActive() && (
					<WhiteTileNb body={comparativeCompetitor.opinions_signature[0].message} avatar={signatureOpinion} />
				)}
				{isVerificationActive() && (
					<WhiteTileNb
						body={comparativeCompetitor.opinions_verifications[0].message}
						avatar={verificationOpinion}
					/>
				)}
			</MainSection100>

			{/* Image comparative */}
			<MainSection100>
				<h2>{comparativeCompetitor.comparative_description_title}</h2>
				<div className="grid-lg-3">
					{isSignActive() &&
						comparativeCompetitor.comparative_description_explanations_signature.map((item, index) => {
							return <ImageGroup key={index} basicInfoWithImage={item} />;
						})}
					{isVerificationActive() &&
						comparativeCompetitor.comparative_description_explanations_verification.map((item, index) => {
							return <ImageGroup key={index} basicInfoWithImage={item} />;
						})}
				</div>
			</MainSection100>

			{/* Why Tecalis */}
			<MainSection100 sectionType="color" sectionColor="gray gray--noframe">
				<h2>{comparativeCompetitor.why_tecalis_title}</h2>
				<div className="grid-lg-4">
					{isSignActive() && (
						<ListHighlight items={comparativeCompetitor.why_tecalis_explanation_signature} />
					)}
					{isVerificationActive() && (
						<ListHighlight items={comparativeCompetitor.why_tecalis_explanation_verification} />
					)}
				</div>
			</MainSection100>

			{/* FaQ */}
			<MainSection100 containerSlim>
				<h3>{comparativeCompetitor.faq_title}</h3>
				{isSignActive() && <ItemGroup items={comparativeCompetitor.faq_signature_items} unboxed />}
				{isVerificationActive() && <ItemGroup items={comparativeCompetitor.faq_verification_items} unboxed />}
			</MainSection100>

			{/* Last News */}
			<MainSection100>
				<h2>{comparativeCompetitor.news_title}</h2>
				<div className="grid-lg-3-h">
					{isSignActive() &&
						comparativeCompetitor.news_item_signature.map((item, index) => {
							const aux = normalizeData(item);
							aux.date = formatDate(aux.date);
							return (
								<CardPost
									key={index}
									lang={lang}
									tag={aux.tag}
									url={aux.url}
									name={aux.title}
									image={aux.image}
									showMore
									date={aux.date}
								/>
							);
						})}
					{isVerificationActive() &&
						comparativeCompetitor.news_item_verification.map((item, index) => {
							const aux = normalizeData(item);
							aux.date = formatDate(aux.date);
							return (
								<CardPost
									key={index}
									lang={lang}
									tag={aux.tag}
									url={aux.url}
									name={aux.title}
									image={aux.image}
									showMore
									date={aux.date}
								/>
							);
						})}
				</div>
			</MainSection100>

			{/* comment */}
			<MainSection100 containerSlim>
				<p className="text--gray-dark">{comparativeCompetitor.info}</p>
			</MainSection100>

			<ReadyToTalk lang={lang} />
		</NewLayout>
	);
};

export default ComparativeCompetitor;

export const comparativeCompetitorQuery = graphql`
	query ($id: String!, $langKey: String!) {
		comparativeCompetitor: strapiComparativeCompetitors(locale: { eq: $langKey }, id: { eq: $id }) {
			url
			seo {
				title
				meta_description
				image_2 {
					localFile {
						publicURL
					}
				}
			}
			logo_tecalis {
				name
				alternativeText
				localFile {
					childImageSharp {
						gatsbyImageData(width: 146, placeholder: BLURRED, formats: [WEBP])
					}
				}
			}
			hero_title
			hero_description
			hero_button_demo
			hero_button_try
			hero_image {
				name
				alternativeText
				localFile {
					childImageSharp {
						gatsbyImageData(width: 640, placeholder: BLURRED, formats: [WEBP], breakpoints: [496, 592])
					}
				}
			}
			comparative_description
			comparative_electronic_signature_button
			comparative_identity_verification_button
			logo_tecalis_comparative {
				name
				alternativeText
				localFile {
					childImageSharp {
						gatsbyImageData(width: 130, placeholder: BLURRED, formats: [WEBP])
					}
				}
			}
			competitor_comparative
			comparative_electronic_signature_elements {
				name
				tecalis
				text_tecalis
				competitor
				text_competitor
			}
			comparative_identity_verification_elements {
				name
				tecalis
				text_tecalis
				competitor
				text_competitor
			}
			comparative_talk_expert_button
			talk_expert_form {
				title
				description
				region
				portal_id
				form_id
			}
			try_form {
				form_email
				or_sign_up
				login_text
				form_name
				form_tc_description
				form_repassword
				form_password
				title
				description
				button_google
				button_email
				form_lastname
				signup_text
				button_signup
				button_login
				remember_me
				forgot_password
				title_login
				description_login
				form_tc_info
			}
			comparative_try_button
			trust_title
			trust_logos_signature {
				alt
				image {
					name
					localFile {
						childImageSharp {
							gatsbyImageData(width: 120, placeholder: BLURRED, formats: [WEBP])
						}
					}
				}
			}
			trust_logos_verification {
				alt
				image {
					name
					localFile {
						childImageSharp {
							gatsbyImageData(width: 120, placeholder: BLURRED, formats: [WEBP])
						}
					}
				}
			}
			opinions_signature {
				name
				job_position
				photo {
					name
					alternativeText
					localFile {
						childImageSharp {
							gatsbyImageData(width: 64, placeholder: BLURRED, formats: [WEBP])
						}
					}
				}
				message
			}
			opinions_verifications {
				name
				job_position
				photo {
					name
					alternativeText
					localFile {
						childImageSharp {
							gatsbyImageData(width: 64, placeholder: BLURRED, formats: [WEBP])
						}
					}
				}
				message
			}
			comparative_description_title
			comparative_description_explanations_signature {
				title
				description
				image {
					name
					alternativeText
					localFile {
						childImageSharp {
							gatsbyImageData(
								width: 704
								placeholder: BLURRED
								formats: [WEBP]
								breakpoints: [352, 432, 444, 512]
							)
						}
					}
				}
			}
			comparative_description_explanations_verification {
				title
				description
				image {
					name
					alternativeText
					localFile {
						childImageSharp {
							gatsbyImageData(
								width: 704
								placeholder: BLURRED
								formats: [WEBP]
								breakpoints: [352, 432, 444, 512]
							)
						}
					}
				}
			}
			why_tecalis_title
			why_tecalis_explanation_signature {
				order
				title
				description
			}
			why_tecalis_explanation_verification {
				order
				title
				description
			}
			faq_title
			faq_signature_items {
				id
				title
				description
			}
			faq_verification_items {
				id
				title
				description
			}
			news_title
			news_item_signature
			news_item_verification
			info
		}
	}
`;
